/**
 * /mcp — Model Context Protocol surfaces.
 *
 * Identity comes from the dashboard-shell auth context; the loader only
 * fetches MCP-specific state.
 */

import { useLoaderData } from "react-router-dom";

import { McpBrowser } from "@/features/mcp/mcp-browser";
import { McpPageHeader } from "@/features/mcp/mcp-page-header";
import { ApiError, api } from "@/lib/api";
import type { ExternalMcpListResponse, ExternalMcpSummary, KbView } from "@/lib/types";

import { useAuthState } from "../lib/auth";

interface McpLoaderData {
  kbs: KbView[];
  externalServers: ExternalMcpSummary[];
  externalServersError: string | null;
}

async function loadExternal(): Promise<{
  externalServers: ExternalMcpSummary[];
  externalServersError: string | null;
}> {
  try {
    const body = await api<ExternalMcpListResponse>("/mcp/external");
    return { externalServers: body.items, externalServersError: null };
  } catch (err) {
    if (err instanceof ApiError) {
      return {
        externalServers: [],
        externalServersError: `Failed to load external MCP (${err.status}): ${err.message}`,
      };
    }
    return {
      externalServers: [],
      externalServersError: err instanceof Error ? err.message : String(err),
    };
  }
}

export async function mcpLoader(): Promise<McpLoaderData> {
  const [kbs, external] = await Promise.all([api<KbView[]>("/kbs"), loadExternal()]);
  return { kbs, ...external };
}

export default function McpRoute() {
  const data = useLoaderData() as McpLoaderData;
  const auth = useAuthState();
  const isAdmin = auth.tenant.role === "team_admin";
  return (
    <section className="space-y-4">
      <McpPageHeader />
      <McpBrowser
        initialTeamSlug={auth.tenant.team_slug}
        initialKbs={data.kbs}
        initialExternalServers={data.externalServers}
        initialExternalServersError={data.externalServersError}
        isAdmin={isAdmin}
      />
    </section>
  );
}
